import {Button, Card, Container, FileButton, Group, Stack, Text, Title} from "@mantine/core";
import {IconLayersDifference} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {TicketCompareDataTable} from "../../components/data-table/TicketCompareDataTable/TicketCompareDataTable";
import {useCallback, useMemo, useState} from "react";
import {notifications} from "@mantine/notifications";
import {
  companySettlementSelector,
  firstOrUndefined,
  ObjectDocumentAssocEntity,
  settlementSelector,
  TtaCompareReportDownloadArg,
  ttaMappedParisSelector,
  useGetCompanySettlementQuery,
  useGetSettlementQuery,
  useLazyMapCompanySettlementTtasQuery
} from "@ots-frontend/ots-data";
import {TicketMappingIdSelect} from "../../components/select/mapping/TicketMappingIdSelect/TicketMappingIdSelect";
import {useNavigate, useSearchParams} from "react-router-dom";
import {skipToken} from "@reduxjs/toolkit/query";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {LoadingPage} from "../../components/page/LoadingPage/LoadingPage";
import {CompanySettlementTitle} from "../../components/profile/CompanySettlementProfile/CompanySettlementProfile";
import {useObjectDocumentAssocMutation} from "../../hooks/useObjectDocumentAssocMutation";
import {
  ObjectDocumentAssocSelectModal
} from "../../components/select/entity/ObjectDocumentAssocSelect/ObjectDocumentAssocSelect";
import {useDisclosure} from "@mantine/hooks";
import {
  FormattedObjectDocumentAssoc
} from "../../components/text/FormattedObjectDocumentAssoc/FormattedObjectDocumentAssoc";
import {useDownloadCompareTtaReport} from "../../hooks/useDownloadTtaCompareReport";
import {useApp} from "../../providers/RegionProvider/RegionProvider";

export type CompanySettlementTicketComparePageProps = {
  id?: string;
}

export const CompanySettlementTicketComparePageRoute = () => {
  const [params, _setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const id = params.get("id") ?? undefined;

  if (!id) {
    navigate(-1);
    return (
      <ErrorPage message={"Company Settlement Not Found"}/>
    );
  }

  return <CompanySettlementTicketComparePage id={id}/>;
};

const FileText = (props: { file?: File | null, document?: ObjectDocumentAssocEntity }) => {
  const {file, document} = props

  if (file) {
    return (
      <>
        {"File: " + file.name}
      </>
    )
  }

  if (document) {
    return (
      <Group spacing="xs">
        {"File: "}
        <FormattedObjectDocumentAssoc value={document}/>
      </Group>
    )
  }

  return (
    <>
      {"File: No File Selected"}
    </>
  )
}

export const CompanySettlementTicketComparePage = (props: CompanySettlementTicketComparePageProps) => {
  const {id} = props;

  const {baseQueryApi} = useApp();
  const {t} = useTranslation(["common"]);

  const [trigger, result] = useLazyMapCompanySettlementTtasQuery();

  const [file, setFile] = useState<File | null>(null)
  const [document, setDocument] = useState<ObjectDocumentAssocEntity | undefined>(undefined)
  const [mappingId, setMappingId] = useState<string | null>(null)
  const [mappingIdError, setMappingIdError] = useState<boolean>(false)

  const [documentSelectOpen, documentSelectHandler] = useDisclosure(false)

  const selectCompanySettlement = useMemo(() => {
    return companySettlementSelector()
  }, []);

  const {companySettlement, isFetching} = useGetCompanySettlementQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      companySettlement: selectCompanySettlement(result),
      isFetching: result.isFetching
    })
  });

  const selectSettlement = useMemo(() => {
    return settlementSelector()
  }, []);

  const {settlement} = useGetSettlementQuery(companySettlement?.settlementId ?? skipToken, {
    selectFromResult: (result) => ({
      settlement: selectSettlement(result)
    })
  });

  const {
    createObjectDocumentAssoc,
  } = useObjectDocumentAssocMutation()

  const {
    downloadCompareTtaReport,
  } = useDownloadCompareTtaReport({baseQueryApi})

  const selectTtaMappedPairs = useMemo(() => {
    return ttaMappedParisSelector();
  }, []);

  const ttaMappedPairs = useMemo(() => {
    return selectTtaMappedPairs(result)
  }, [selectTtaMappedPairs, result])

  const onSubmit = useCallback(() => {
    setMappingIdError(!mappingId)

    if (companySettlement && mappingId && (file || document)) {
      const arg = {
        mappingFileId: mappingId,
        file: file,
        objectDocumentAssocId: document?.id,
        id: companySettlement.id,
      }

      trigger(arg)
        .unwrap()
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
          })
        );
    }
  }, [companySettlement, mappingId, file, document, trigger, t]);

  const onFileSelect = useCallback((file: File | null) => {
    setFile(file)
    setDocument(undefined)
  }, [])

  const onStoreFileClick = useCallback(() => {
    if (companySettlement && file) {
      createObjectDocumentAssoc({
        objectId: companySettlement.id,
        objectType: companySettlement.entityType,
        file: file
      }, undefined)
    }
  }, [companySettlement, file, createObjectDocumentAssoc]);

  const onDocumentSelect = useCallback((objectDocumentAssocs: ObjectDocumentAssocEntity[] | undefined) => {
    setDocument(firstOrUndefined(objectDocumentAssocs))
    setFile(null)
    documentSelectHandler.close()
  }, [documentSelectHandler]);

  const onMappingChange = useCallback((value: string | null) => {
    setMappingId(value)
  }, [])

  const onDownloadButtonClick = useCallback(() => {
    const arg: TtaCompareReportDownloadArg = {
      ttaMappedPairs: ttaMappedPairs?.map(p => p.plain()) ?? [],
      regionId: companySettlement?.regionId,
    }

    downloadCompareTtaReport(arg, undefined)
  }, [ttaMappedPairs, companySettlement?.regionId, downloadCompareTtaReport]);

  if (isFetching) {
    return (
      <LoadingPage/>
    );
  }

  if (!companySettlement) {
    return (
      <ErrorPage message={"Company Settlement Not Found"}/>
    );
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconLayersDifference/>
                      <Title order={4}>
                        <Group spacing="xs">
                          {t("common.page.companysettlement.ticket.compare.title", "Ticket Compare:")}
                          <CompanySettlementTitle
                            withPrefix={false}
                            companySettlement={companySettlement}
                            settlement={settlement}/>
                        </Group>
                      </Title>
                    </Group>
                    <Button onClick={onStoreFileClick} disabled={!file}>
                      {t("common.action.store", "Store")}
                    </Button>
                  </Group>

                  <Group position="center" mb="lg">
                    <TicketMappingIdSelect
                      label="Mapping"
                      placeholder="Mapping"
                      withinPortal={true}
                      onChange={onMappingChange}
                      defaultValue={mappingId}
                      error={mappingIdError}/>
                  </Group>
                </Card>

                <Group position="left">
                  <Text size="sm" align="center">
                    <FileText file={file} document={document}/>
                  </Text>
                </Group>

                <Group position="apart">

                  <Group position="left">
                    <Button variant="outline" onClick={() => documentSelectHandler.open()}>
                      {t("common.action.selectexistingfile", "Select Existing File")}
                    </Button>
                    <FileButton onChange={onFileSelect}>
                      {(props) => (
                        <Button {...props} variant="outline">
                          {t("common.action.uploadfile", "Upload File")}
                        </Button>
                      )}
                    </FileButton>
                    <Button onClick={onSubmit} disabled={!file && !document}>
                      {t("common.action.submit", "Submit")}
                    </Button>
                  </Group>

                  <Button onClick={onDownloadButtonClick}>
                    {t("common.action.download", "Download")}
                  </Button>
                </Group>

                <TicketCompareDataTable
                  withBorder
                  withColumnBorders={true}
                  records={ttaMappedPairs}
                  recordsPerPageOptions={[25]}
                  fetching={result.isLoading || result.isFetching}/>

              </Stack>

              <ObjectDocumentAssocSelectModal
                select={{
                  withinPortal: true,
                }}
                entity={companySettlement}
                size="lg"
                opened={documentSelectOpen}
                onClose={documentSelectHandler.close}
                onSubmit={onDocumentSelect}
              />

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
